import React, { useCallback, useEffect, useRef, useState } from "react";
import useEmblaCarousel, {
  EmblaCarouselType,
  EmblaOptionsType
} from "embla-carousel-react";
import { NextButton, PrevButton } from "./CarouselButtons";
import Icon from "../../../../../../components/Icon";
import { CarouselThumb } from "./CarouselThumb";
import { RenderWhen } from "../../../../../../components";
import CarouselSlide from "./CarouselSlide";

type Props = {
  imagePaths: string[];
  options?: EmblaOptionsType;
  imageGroups?: [{ length: number; type: string; sha1: string; url: string }];
};

const AUTOPLAY_DURATION = 5000;

const RlCloudSandboxCarousel: React.FC<Props> = ({
  imagePaths,
  options,
  imageGroups
}) => {
  const [emblaRef, emblaApi] = useEmblaCarousel(options);
  const [emblaThumbsRef, emblaThumbsApi] = useEmblaCarousel({
    containScroll: "keepSnaps",
    dragFree: true
  });
  const rootRef = useRef<HTMLDivElement>(null);
  const thumbsRef = useRef<Array<HTMLDivElement | null>>([]);

  const [prevBtnDisabled, setPrevBtnDisabled] = useState(true);
  const [nextBtnDisabled, setNextBtnDisabled] = useState(true);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [toggleAutoPlay, setAutoPlay] = useState(false);

  const scrollPrev = useCallback(
    () => emblaApi && emblaApi.scrollPrev(),
    [emblaApi]
  );
  const scrollNext = useCallback(
    () => emblaApi && emblaApi.scrollNext(),
    [emblaApi]
  );

  useEffect(() => {
    if (!emblaApi || !toggleAutoPlay) return undefined;
    const intervalId = setInterval(
      () => (emblaApi.canScrollNext() ? scrollNext() : emblaApi.scrollTo(0)),
      AUTOPLAY_DURATION
    );

    return () => intervalId && clearInterval(intervalId);
  }, [toggleAutoPlay, scrollNext, emblaApi]);

  // native scroll used because emblaThumbsApi breaks the
  // horizontal scroll (using translate3d dynamically)
  const onThumbSelect = useCallback(
    (index: number) => {
      if (!emblaApi) return;
      emblaApi.scrollTo(index);
      thumbsRef.current[index]?.scrollIntoView({
        behavior: "smooth",
        inline: "center"
      });
    },
    [emblaApi]
  );

  const onSelect = useCallback(
    (api: EmblaCarouselType) => {
      if (!emblaApi || !emblaThumbsApi) return;
      onThumbSelect(emblaApi.selectedScrollSnap());
      setSelectedIndex(emblaApi.selectedScrollSnap());
      setPrevBtnDisabled(!api.canScrollPrev());
      setNextBtnDisabled(!api.canScrollNext());
    },
    [emblaApi, emblaThumbsApi, setSelectedIndex, onThumbSelect]
  );
  useEffect(() => {
    if (!emblaApi || !emblaThumbsApi) return;
    onSelect(emblaApi);
    emblaApi.on("reInit", onSelect);
    emblaApi.on("select", onSelect);
    emblaThumbsApi.internalEngine().dragHandler.destroy(); // disable drag on thumbs (breaks horizontal scroll)
    rootRef?.current?.focus();
  }, [emblaApi, emblaThumbsApi, onSelect]);

  const handleScroll = (event: React.WheelEvent<HTMLDivElement>) => {
    const container = emblaThumbsApi?.rootNode();
    container?.scrollTo({ left: container?.scrollLeft + event.deltaY });
  };

  const handleArrowKeyPress = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "ArrowLeft") emblaApi?.scrollPrev();
    if (event.key === "ArrowRight") emblaApi?.scrollNext();
  };

  const getIndexLimits = () => {
    if (!imageGroups?.length) return undefined;
    let indexLimit = 0;
    return imageGroups.map(element => {
      indexLimit += element.length;
      return indexLimit;
    });
  };

  const getThumbLabel = (index: number, numTotalImages: number) => {
    const indexLimits = getIndexLimits();
    if (!imageGroups?.length || !indexLimits)
      return `${index + 1} / ${numTotalImages}`;
    for (let i = 0; i < indexLimits?.length; i++) {
      if (index + 1 <= indexLimits[i])
        return `${(i > 0 ? index - indexLimits[i - 1] : index) + 1} / ${
          imageGroups[i].length
        }`;
    }
    return undefined;
  };

  const getGroupLabels = (index: number) => {
    if (!imageGroups?.length) return undefined;
    if (index === 0) {
      return {
        type: (imageGroups[0].type === "url" && "URL") || imageGroups[0].type,
        name: imageGroups[0].url || imageGroups[0].sha1
      };
    }
    const indexLimits = getIndexLimits();
    if (!indexLimits) return undefined;
    for (let i = 0; i < indexLimits?.length - 1; i++) {
      if (index === indexLimits[i]) {
        return {
          type:
            (imageGroups[i + 1].type === "url" && "URL") ||
            imageGroups[i + 1].type,
          name: imageGroups[i + 1].url || imageGroups[i + 1].sha1
        };
      }
    }
    return undefined;
  };

  const showGroupDivider = (index: number) => {
    const indexLimits = getIndexLimits();
    return indexLimits?.includes(index);
  };

  return (
    <div
      className="rl-cloud-sandbox__screenshots-carousel"
      onKeyDown={handleArrowKeyPress}
      ref={rootRef}
      tabIndex={0}
      data-testid="rlcs_screenshots_carousel"
    >
      <div className="embla">
        <PrevButton onClick={scrollPrev} disabled={prevBtnDisabled} />
        <div className="embla__viewport" ref={emblaRef}>
          <div className="embla__container">
            {imagePaths.map(imgPath => (
              <CarouselSlide
                key={imgPath}
                imgPath={imgPath}
                toggleAutoPlay={toggleAutoPlay}
              />
            ))}
          </div>
        </div>
        <NextButton onClick={scrollNext} disabled={nextBtnDisabled} />
      </div>
      <div className="embla-thumbs">
        <div
          className="embla-thumbs__viewport"
          onWheel={handleScroll}
          ref={emblaThumbsRef}
        >
          <div className="embla-thumbs__container">
            {imagePaths.map((imgPath, index) => (
              <CarouselThumb
                ref={el => (thumbsRef.current[index] = el)}
                onClick={() => onThumbSelect(index)}
                selected={index === selectedIndex}
                imgSrc={imgPath}
                thumbLabel={getThumbLabel(index, imagePaths.length)}
                groupLabels={getGroupLabels(index)}
                showGroupDivider={showGroupDivider(index)}
                key={imgPath}
                overlayProp={
                  <RenderWhen condition={index === selectedIndex}>
                    <Icon
                      onClick={() => setAutoPlay(prev => !prev)}
                      name={toggleAutoPlay ? "pause" : "run"}
                      className="rl-cloud-sandbox-autoplay-icon"
                    />
                  </RenderWhen>
                }
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RlCloudSandboxCarousel;
